import { useState } from "react";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
} from "recharts";
import {
  FaChartLine,
  FaBell,
  FaBars,
  FaFilter,
} from "react-icons/fa";
import { AiOutlineCalendar, AiOutlineClose } from "react-icons/ai";

const data = [
  { name: "Jan", income: 10000, expense: 5000 },
  { name: "Feb", income: 15000, expense: 8000 },
  { name: "Mar", income: 20000, expense: 10000 },
  { name: "Apr", income: 17000, expense: 9000 },
  { name: "May", income: 22000, expense: 11000 },
  { name: "Jun", income: 24000, expense: 13000 },
  { name: "Jul", income: 21000, expense: 10000 },
  { name: "Aug", income: 23000, expense: 11000 },
];

const COLORS = ["#e11d48", "#fca5a5"];

const statCards = [
  { title: "Total Revenue", value: 15 },
  { title: "Total Income", value: 15 },
  { title: "Total Expense", value: 25 },
  { title: "Payment Receivable", value: 15 },
  { title: "Payment Payable", value: 25 },
];

const DashboardPage = () => {
  const [activeTab, setActiveTab] = useState("Earnings");
  const [timeTab, setTimeTab] = useState("Yearly");
  const [showFilter, setShowFilter] = useState(false);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const handleReset = () => {
    setStartDate("");
    setEndDate("");
    setShowFilter(false);
  };

  const handleApply = () => {
    console.log("Start Date:", startDate);
    console.log("End Date:", endDate);
    setShowFilter(false);
  };

  return (
    <div className="p-4 bg-white">
      {/* Stat Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 mb-4">
        {statCards.map((card, idx) => (
          <div
            key={idx}
            className="bg-rose-600 text-white text-center rounded-md p-2"
          >
            <p className="text-sm font-medium">{card.title}</p>
            <p className="text-lg font-bold">{card.value}</p>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div className="flex flex-wrap gap-2 mb-4">
        {["Earnings", "Orders", "Sale Count", "Sale Value"].map((tab) => (
          <button
            key={tab}
            className={`px-3 py-1 rounded-full text-sm font-medium border ${
              activeTab === tab
                ? "bg-rose-600 text-white border-rose-600"
                : "border-gray-300 text-gray-700"
            }`}
            onClick={() => setActiveTab(tab)}
          >
            {tab}
          </button>
        ))}
         {/* Filter Icon */}
            <button
              onClick={() => setShowFilter(true)}
              className="p-2 rounded-full bg-gray-200 text-gray-600 hover:bg-rose-100 hover:text-rose-600"
              title="Filter by date"
            >
              <FaFilter size={14} />
            </button>
      </div>

      {/* Earnings Chart */}
      <div className="bg-white rounded-md shadow p-4 mb-6">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2 mb-2">
          <h3 className="font-semibold text-xs md:text-lg ">Earning Statistics</h3>
          <div className="flex flex-wrap gap-2 items-center text-sm">
            {["Hourly", "Weekly", "Monthly", "Yearly"].map((t) => (
              <button
                key={t}
                onClick={() => setTimeTab(t)}
                className={`px-2 py-1 rounded-full ${
                  timeTab === t
                    ? "bg-rose-600 text-white"
                    : "bg-gray-200 text-gray-600"
                }`}
              >
                {t}
              </button>
            ))}

           
          </div>
        </div>

        <ResponsiveContainer width="100%" height={200}>
          <AreaChart data={data} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
            <XAxis dataKey="name" />
            <YAxis />
            <Tooltip />
            <Area type="monotone" dataKey="income" stroke="#f43f5e" fill="#fda4af" />
            <Area type="monotone" dataKey="expense" stroke="#9f1239" fill="#fecdd3" />
          </AreaChart>
        </ResponsiveContainer>
      </div>

      {/* Monthly Pie Charts */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
        {data.map((month, idx) => (
          <div
            key={idx}
            className="border rounded-md p-3 text-center text-xs font-medium"
          >
            <p className="mb-1 font-bold">{month.name}</p>
            <ResponsiveContainer width="100%" height={100}>
              <PieChart>
                <Pie
                  dataKey="value"
                  data={[
                    { name: "Income", value: month.income },
                    { name: "Expense", value: month.expense },
                  ]}
                  outerRadius={40}
                  label
                >
                  {COLORS.map((color, index) => (
                    <Cell key={`cell-${index}`} fill={color} />
                  ))}
                </Pie>
                <Legend verticalAlign="bottom" height={20} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        ))}
      </div>

      {/* Filter Modal */}
      {showFilter && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 w-[90%] max-w-md relative shadow-lg">
            {/* Close Button */}
            <button
              className="absolute top-2 right-2 text-primary"
              onClick={() => setShowFilter(false)}
            >
              <AiOutlineClose size={20} />
            </button>

            <h4 className="text-lg font-bold mb-4">Filter by Date</h4>

            <label className="block text-sm mb-2">Start Date</label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="w-full border p-2 rounded-md text-sm mb-4"
            />

            <label className="block text-sm mb-2">End Date</label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="w-full border p-2 rounded-md text-sm mb-6"
            />

            <div className="flex justify-between">
              <button
                onClick={handleReset}
                className="border px-4 py-2 rounded-md text-sm"
              >
                Reset
              </button>
              <button
                onClick={handleApply}
                className="bg-rose-600 text-white px-4 py-2 rounded-md text-sm"
              >
                Apply
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DashboardPage;
